import { Download, RotateCcw, Search, Sparkles } from 'lucide-react';

import type {
  AdxFilter,
  BooleanFilter,
  MacdFilter,
  OpportunityFilterState,
  RsiFilter,
  VolumeFilter,
} from '@/features/opportunities/opportunityFilterModel';
import type { NewsSentimentType, TradeSignal } from '@/models/marketData';

interface OpportunityFiltersProps {
  filters: OpportunityFilterState;
  onChange: (next: OpportunityFilterState) => void;
  onReset: () => void;
  onExport: () => void;
  exporting: boolean;
}

export const OpportunityFilters = ({
  filters,
  onChange,
  onReset,
  onExport,
  exporting,
}: OpportunityFiltersProps) => {
  const patch = <Key extends keyof OpportunityFilterState>(
    key: Key,
    value: OpportunityFilterState[Key],
  ) => onChange({ ...filters, [key]: value });

  return (
    <div className="radar-controls" aria-label="Opportunity filters">
      <label className="filter-field filter-field--wide">
        <Search size={15} aria-hidden="true" />
        <span className="sr-only">Global search</span>
        <input
          type="search"
          value={filters.globalSearch}
          onChange={(event) => patch('globalSearch', event.target.value)}
          placeholder="Global search: symbols, news, rationale…"
        />
      </label>
      <label className="filter-field filter-field--wide">
        <span className="sr-only">Symbol or company search</span>
        <input
          type="search"
          value={filters.companySearch}
          onChange={(event) => patch('companySearch', event.target.value)}
          placeholder="Symbol or company"
        />
      </label>
      <label className="filter-field">
        <span className="sr-only">Signal filter</span>
        <select
          value={filters.signal}
          onChange={(event) =>
            patch(
              'signal',
              event.target.value === 'all' ? 'all' : (Number(event.target.value) as TradeSignal),
            )
          }
        >
          <option value="all">All signals</option>
          <option value="5">Constructive</option>
          <option value="4">Positive</option>
          <option value="3">Watchlist</option>
        </select>
      </label>
      <label className="filter-field">
        <span className="sr-only">Minimum score</span>
        <select
          value={filters.minScore}
          onChange={(event) => patch('minScore', +event.target.value)}
        >
          <option value="0">Any score</option>
          <option value="90">Score ≥ 90</option>
          <option value="80">Score ≥ 80</option>
          <option value="70">Score ≥ 70</option>
          <option value="60">Score ≥ 60</option>
        </select>
      </label>
      <label className="filter-field">
        <span className="sr-only">RSI filter</span>
        <select
          value={filters.rsi}
          onChange={(event) => patch('rsi', event.target.value as RsiFilter)}
        >
          <option value="all">Any RSI</option>
          <option value="momentum">RSI 55–70</option>
          <option value="overheated">RSI ≥ 70</option>
          <option value="below-55">RSI &lt; 55</option>
        </select>
      </label>
      <label className="filter-field">
        <span className="sr-only">ADX filter</span>
        <select
          value={filters.adx}
          onChange={(event) => patch('adx', event.target.value as AdxFilter)}
        >
          <option value="all">Any ADX</option>
          <option value="strong">ADX ≥ 25</option>
          <option value="weak">ADX &lt; 25</option>
        </select>
      </label>
      <label className="filter-field">
        <span className="sr-only">Volume filter</span>
        <select
          value={filters.volume}
          onChange={(event) => patch('volume', event.target.value as VolumeFilter)}
        >
          <option value="all">Any volume</option>
          <option value="surge">Volume ≥ 1.5×</option>
          <option value="normal">Volume &lt; 1.5×</option>
        </select>
      </label>
      <label className="filter-field">
        <span className="sr-only">SuperTrend filter</span>
        <select
          value={filters.superTrend}
          onChange={(event) => patch('superTrend', event.target.value as BooleanFilter)}
        >
          <option value="all">Any SuperTrend</option>
          <option value="yes">ST bullish</option>
          <option value="no">ST bearish</option>
        </select>
      </label>
      <label className="filter-field">
        <span className="sr-only">EMA alignment filter</span>
        <select
          value={filters.emaAlignment}
          onChange={(event) => patch('emaAlignment', event.target.value as BooleanFilter)}
        >
          <option value="all">Any EMA setup</option>
          <option value="yes">EMA aligned</option>
          <option value="no">EMA mixed</option>
        </select>
      </label>
      <label className="filter-field">
        <span className="sr-only">MACD filter</span>
        <select
          value={filters.macd}
          onChange={(event) => patch('macd', event.target.value as MacdFilter)}
        >
          <option value="all">Any MACD</option>
          <option value="bullish">MACD bullish</option>
          <option value="below-signal">Below signal</option>
          <option value="fresh">Fresh crossover</option>
        </select>
      </label>
      <label className="filter-field">
        <span className="sr-only">News sentiment filter</span>
        <select
          value={filters.newsSentiment}
          onChange={(event) =>
            patch('newsSentiment', event.target.value as NewsSentimentType | 'all')
          }
        >
          <option value="all">Any news sentiment</option>
          <option value="positive">Positive news</option>
          <option value="neutral">Neutral news</option>
          <option value="negative">Negative news</option>
          <option value="mixed">Mixed news</option>
          <option value="none">No related news</option>
        </select>
      </label>
      <div className="radar-actions">
        <button
          type="button"
          className={`action-button action-button--top ${filters.topTen ? 'is-active' : ''}`}
          onClick={() => patch('topTen', !filters.topTen)}
          aria-pressed={filters.topTen}
        >
          <Sparkles size={15} /> {filters.topTen ? 'Show all' : 'Top 10'}
        </button>
        <button type="button" className="action-button" onClick={onReset}>
          <RotateCcw size={15} /> Reset
        </button>
        <button
          type="button"
          className="action-button action-button--export"
          onClick={onExport}
          disabled={exporting}
        >
          <Download size={15} /> {exporting ? 'Preparing…' : 'Export'}
        </button>
      </div>
    </div>
  );
};
